/**
 * Helpers for the "first-time publish" guided flow.
 *
 * The first time a user publishes a workflow created **via onboarding** we
 * walk them through Build → Test → Fund → Publish with a left-side stepper
 * plus an "Add funds to publish" CTA in the run summary modal. After the
 * publish flag flips, subsequent edits get the lighter `FundsAdvisoryPill`
 * advisory only.
 *
 * The funnel is **scoped to onboarding-originated workflows only** —
 * templates opened directly and manually-created workflows skip the stepper
 * because experienced users don't need the handholding (and it crowds the
 * canvas).
 *
 * Originate-from-onboarding is signalled with a URL query parameter
 * (`?fromOnboarding=1`) instead of localStorage so:
 *   - The flow survives a page refresh while the user stays on the editor
 *     (the param is part of the URL).
 *   - Leaving the editor and coming back later (without the param) drops
 *     the user out of the funnel — there's no stale per-workflow state to
 *     re-trigger the stepper after the user has moved on.
 *
 * The wizard appends the param when it navigates to the templates page;
 * `useTemplateAction` propagates it onto the editor URL when a template is
 * promoted into a workflow. The editor reads it from `useSearchParams` and
 * threads it through `isWorkflowInFirstPublishFunnel`.
 *
 * Storage contract:
 *   key:   `b3os:workflows:firstPublishCompleted:<workflowId>`
 *   value: `"1"` once the workflow has been successfully published.
 */

const STORAGE_PREFIX = "b3os:workflows:firstPublishCompleted:";

/** Query parameter that marks an editor visit as originating from onboarding. */
export const FROM_ONBOARDING_QUERY_PARAM = "fromOnboarding";

function storageKey(workflowId: string): string {
  return `${STORAGE_PREFIX}${workflowId}`;
}

/**
 * `true` iff the workflow has previously been published (per the localStorage
 * flag). Server-side renders return `false` so the stepper doesn't flash on
 * hydration before localStorage is available — `useFirstTimePublishFlow`
 * applies the SSR-safe default through `useEffect`.
 */
export function hasCompletedFirstPublish(workflowId: string | undefined | null): boolean {
  if (!workflowId || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(storageKey(workflowId)) === "1";
  } catch {
    return false;
  }
}

/** Mark the workflow's first-publish flow as complete. Idempotent. */
export function markFirstPublishCompleted(workflowId: string | undefined | null): void {
  if (!workflowId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(workflowId), "1");
  } catch {
    // localStorage can throw in private modes / quota — fall through.
  }
}

/**
 * Determine whether a workflow is in the "first-time publish" funnel.
 *
 * A workflow qualifies when:
 *   1. It exists (has an id),
 *   2. It has never been published (`!hasLiveVersion`),
 *   3. The localStorage publish-completed flag has not been flipped yet, and
 *   4. The current editor URL carries `?fromOnboarding=1` (caller passes
 *      the resolved boolean — the helper itself doesn't read the URL so
 *      it stays trivially testable and SSR-friendly).
 *
 * Condition (4) is what restricts the stepper to onboarding-driven
 * workflows: templates opened directly and manually-created workflows skip
 * the funnel and only get the lightweight `FundsAdvisoryPill`. Once
 * `markFirstPublishCompleted` is called after a successful publish, (3)
 * becomes false and the funnel hides for that workflow forever (even if
 * the user keeps the `fromOnboarding` query param around).
 */
export function isWorkflowInFirstPublishFunnel(args: {
  workflowId: string | undefined | null;
  hasLiveVersion: boolean | undefined | null;
  fromOnboarding: boolean;
}): boolean {
  if (!args.workflowId) return false;
  if (args.hasLiveVersion) return false;
  if (hasCompletedFirstPublish(args.workflowId)) return false;
  return args.fromOnboarding;
}
